import { Currency } from '../shared/drivers/currency.js';
import { getTopCareerItems } from '../shared/helpers/jobs.js';
import { createUrlWithParams } from '../shared/helpers/util.js';
import Activity from '../shared/models/activity/activity.js';

export const headers = {
  'Content-Type': 'application/json',
};

export default class Client {

  constructor({ session }) {
    this.session = session;
  }

  async callWithRetries(fn, options = { maxRetries: 3 }, attempts = 0) {
    const backoff = amount => new Promise(resolve => setTimeout(resolve, Math.round(2000 + (1000 * (amount - 1)))));

    if (attempts > options.maxRetries) {
      throw new Error('Out of retries');
    }

    try {
      attempts += 1;
      const result = await fn();
      return result;
    } catch {
      await backoff(attempts);
      return await this.callWithRetries(fn, options, attempts);
    }
  }

  /**
   * This search is taking a naive approach of just returning everything.
   * It will get all activities using large pages of 500 at a time.
   */
  async activityList(category, filters = {}, fields) {
    const activities = [];
    let from = 0;
    let numOfRecords;
    const size = 500;

    // Query 500 records at a time, stop querying when all records have been retrieved
    do {

      // eslint-disable-next-line no-await-in-loop
      const docs = await this.searchActivities({ from, size, filters, category, fields });
      const { hits, total } = docs;
      activities.push(...hits.map(hit => new Activity(hit)));
      from += size;
      numOfRecords = numOfRecords || total.value;
    } while (from < numOfRecords);

    return activities;
  }

  addCourses(programId, addedCourses) {
    return this.session.request(`activities/relation/${programId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ addedCourses }),
    });
  }

  applyActivity({ activityId, ...applicationData }) {
    const allowedKeys = ['extraAttributes', 'locationDetails', 'message', 'parentIds', 'skuId', 'requestedApprover', 'tax', 'tosVersion'];
    const payload = allowedKeys.reduce((acc, key) => {
      const itemKey = key === 'locationDetails' ? 'location' : key;
      if (applicationData[key] !== undefined) acc[itemKey] = applicationData[key];
      return acc;
    }, {});

    return this.session.request(`activities/${activityId}/apply`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  }

  applyExternalActivity(application) {
    return this.session.request('activities/external-activity/apply', {
      method: 'POST',
      headers,
      body: JSON.stringify(application),
    });
  }

  calculateTaxRates(taxData) {
    return this.session.request('tax/rates', {
      method: 'POST',
      headers,
      body: JSON.stringify({ taxData }),
    });
  }

  // Employee initiated cancellations
  revokeApplication(uuid, cancelReason) {
    const completedDate = new Date().toISOString();
    return this.session.request(`requests/${uuid}/revoke`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ completedDate, cancelReason }),
    });
  }

  addPreRegistrationDate({ applicationUuid, preRegistrationDate }) {
    return this.session.request(`requests/${applicationUuid}/pre-registration`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ preRegistrationDate }),
    });
  }

  completeApplication(uuid, enrollDate, completedDate, completionStatus, cancelReason, refundPct, updateReason) {
    return this.session.request(`requests/${uuid}/complete`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ enrollDate, completedDate, completionStatus, cancelReason, refundPct, updateReason }),
    });
  }

  contactSupport(userInfo, message, category) {
    return this.session.request('support/contact', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ userInfo, message, category }),
    });
  }

  convertCostToEmployee(applicationUuid, toCurrency) {
    return this.session.request(createUrlWithParams(`requests/${applicationUuid}/cost-to-employee`, {
      in: toCurrency,
    }), {
      method: 'GET',
      headers,
    });
  }

  convertCurrency(from, to, amount) {
    return Currency.convert(from, to, amount);
  }

  countReportAdoption(opts) {
    return this.session.request('report/adoption/execute/count', {
      method: 'PUT',
      headers,
      body: JSON.stringify(opts),
    });
  }

  countryNames() {
    const lang = document.documentElement.getAttribute('lang');
    return this.session.request(`lang/countries/${lang}`);
  }

  createLoginLink(opts) {
    return this.session.domainRequest('login/magic/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(opts),
    });
  }

  createGuestLogin(opts) {
    return this.session.domainRequest('login/magic/guest/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(opts),
    });
  }

  createPaymentFlow({ uuid }) {
    return this.session.request('payment/create', {
      method: 'POST',
      headers,
      body: JSON.stringify({ applicationUuid: uuid }),
    });
  }

  createRequestExport(appliedRequestFilters) {
    return this.session.request('requests/export', {
      method: 'POST',
      headers,
      body: JSON.stringify({ appliedRequestFilters }),
    });
  }

  createInsightsRequestExport(type, filter, options) {
    return this.session.request(`report/${type}/export`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ filter, options }),
      });
  }

  createJobsExport(tenantId) {
    return this.session.request(`jobs/${tenantId}/export`, {
      method: 'GET',
      headers,
    });
  }

  checkFirstTimeUser(userGuid) {
    return this.session.request(`users/is-first-session/${userGuid}`, {
      method: 'GET',
      headers,
    });
  }

  checkJobsExportStatus(tenantId) {
    return this.session.request(`jobs/${tenantId}/export/status`, {
      method: 'GET',
      headers,
    });
  }

  checkActivitiesSearchStatus(tenantId, userId) {
    return this.session.request(`activities/search/export/${tenantId}/${userId}/status`, {
      method: 'GET',
      headers,
    });
  }

  checkApplicationOverviewReportStatus(tenantId, userId) {
    return this.session.request(`requests/export/${tenantId}/${userId}/status`, {
      method: 'GET',
      headers,
    });
  }

  checkInsightsExportStatus(type, tenantId, userId) {
    return this.session.request(`report/${type}/export/${tenantId}/${userId}/status`, {
      method: 'GET',
      headers,
    });
  }

  createTenant(tenant) {
    return this.session.request('tenants', {
      method: 'POST',
      headers,
      body: JSON.stringify({ tenant }),
    });
  }

  deleteTenantSSO(tenantSSO) {
    return this.session.request(`tenants/${tenantSSO.tenantId}/sso/${tenantSSO.id}`, {
      method: 'DELETE',
      headers,
    });
  }

  executeReportWidget(type, opts) {
    return this.session.request(`report/${type}/execute`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(opts),
    });
  }

  getReportTaskStatus(type, taskExecutionId) {
    return this.session.request(`report/${type}/executions/${taskExecutionId}`, {
      method: 'GET',
      headers,
    });
  }

  // Extracts skills from activity description and saves extracted skills to activity
  extractSkills(activityId) {
    return this.session.request('activities/extract-skills', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ activityId }),
    });
  }

  fetchActivity(activityId) {
    return this.session.request(`activities/${activityId}`);
  }

  fetchTenant(tenantId) {
    return this.session.request(`tenants/${tenantId}`);
  }

  fetchTenantSSO(tenantId) {
    return this.session.request(`tenants/${tenantId}/sso`);
  }

  fetchTenantAttributeSources(ssoId) {
    return this.session.request(`tenants/${ssoId}/sso/attribute-sources`);
  }

  fetchCoordinatesFromPostalCodes(postalCode, country) {
    const response = this.session.request('location/coordinates', {
      method: 'POST',
      headers,
      body: JSON.stringify({ postalCode, country }),
    });
    return response;
  }

  generateActivityHierarchy(tenantId, providerId) {
    return this.session.request(`activities/generate-hierarchy/${tenantId}/${providerId}`, {
      method: 'GET',
      headers,
    });
  }

  getAccountConnection(userGuid, providerId) {
    const url = createUrlWithParams('connection', { userGuid, providerId });
    return this.session.request(url);
  }

  getActivityCompletion(activityId) {
    return this.session.request(`activities/${activityId}/completion`);
  }

  getActivityReport(tenantId, userId) {
    return this.session.request(`activities/search/export/${tenantId}/${userId}`, {
      method: 'GET',
      headers,
    });
  }

  getAllStreams(tenantId) {
    return this.session.request(`tenants/${tenantId}/all-streams`);
  }

  async getStreamFromPath(tenantId, path) {
    const allStreams = await this.session.request(`tenants/${tenantId}/all-streams`);
    return allStreams?.customStreams?.find(stream => stream.path === path)
      || allStreams?.dynamicStreams?.find(stream => stream.path === path)
      || allStreams?.staticStreams?.find(stream => stream.path === path);
  }

  /**
 * Retrieves an application by its UUID.
 * Optionally includes related provider and activity information based on query parameters.
 *
 * NOTE: If you inlcude optional parameters, the response will be an object with the following properties:
 * - application: The application object. (included as a separate property ONLY if any flags are true)
 * - provider: The provider object. (if withProvider is true)
 * - activity: The activity object. (if withActivity is true)
 * - tenant: The tenant object. (if withTenant is true)
 *
 * @param {string} uuid - The unique identifier of the application.
 * @param {Object} params - An object containing optional query parameters.
 * @param {boolean} [params.withProvider=false] - If true, includes provider information in the response.
 * @param {boolean} [params.withActivity=false] - If true, includes activity information in the response.
 * @param {boolean} [params.withApplicantTenant=false] - If true, includes applicant tenant information in the response
 */
  getApplication(uuid, params = {}) {
    const url = createUrlWithParams(`requests/${uuid}`, params);
    return this.session.request(url);
  }

  getApplications(userGuid, includeRequested = false, tenantIdFilter) {
    const url = createUrlWithParams('requests', { userGuid, includeRequested, tenantIdFilter });
    return this.session.request(url);
  }

  getApplicationOverviewReportURL(tenantId, userId) {
    return this.session.request(`requests/export/${tenantId}/${userId}`, {
      method: 'GET',
      headers,
    });
  }

  getInsightsReport(type, tenantId, userId) {
    return this.session.request(`report/${type}/export/${tenantId}/${userId}`, {
      method: 'GET',
      headers,
    });
  }

  searchApplications({ includeAggregations, filters, from, size, sort, aggregateFields }) {
    const url = createUrlWithParams('requests/search', { from, size, filters, sort, includeAggregations, aggregateFields });
    return this.session.domainRequest(url);
  }

  searchUsers(searchParams) {
    const url = createUrlWithParams('users/search', searchParams);
    return this.session.request(url);
  }

  searchEmployees(searchParams) {
    const url = createUrlWithParams('users/employees', searchParams);
    return this.session.request(url);
  }

  countUsers(searchParams) {
    const url = createUrlWithParams('users/count', searchParams);
    return this.session.request(url);
  }

  getDepartments(tenantIds) {
    const ids = Array.isArray(tenantIds) ? tenantIds : [tenantIds];
    return this.session.request('users/departments', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenantIds: ids }),
    });
  }

  inviteUsers(tenantId, emails, execute = false) {
    const body = { emails };
    if (execute) body.execute = execute;
    return this.session.request(`users/${tenantId}/invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  }

  resendInvitation(userGuid) {
    return this.session.request(`users/reinvite/${userGuid}`, { method: 'PUT' });
  }

  cancelInvitation(userGuid) {
    return this.session.request(`users/cancel/${userGuid}`, { method: 'POST' });
  }

  /**
   * @param activityId
   * @param [includeAllApplications]
   * @returns Array<{}>
   */
  getApplicationsForActivity(activityId, includeAllApplications) {
    const url = createUrlWithParams(`requests/activity/${activityId}`, { includeAllApplications });
    return this.session.request(url);
  }

  /**
   * Currently this function assumes that there is only one application per activity. Once that changes the code that calls this function will have to be updated
   * @param activityId
   * @param userGuid
   * @returns Array<{}>
   */
  getApplicationsForActivityForUser(activityId, userGuid) {
    return this.session.request(`requests/activity/${activityId}/${userGuid}`);
  }

  getRequestedApproverDetails(tenantId, applicationId) {
    return this.session.request(`users/${tenantId}/${applicationId}/requested-approver`);
  }

  // Get child activities (courses) for a given parent activity (program)
  getProgramCourses(parentId) {
    return this.session.request(`activities/${parentId}/children`);
  }

  // Get parent activities (programs) for a given child activity (course)
  getCourseParents(childId) {
    return this.session.request(`activities/${childId}/parents`);
  }

  getAudit(tenantId, pageSize, action, timeframe, search, from, to, nextToken) {
    const params = { tenantId, pageSize, action, timeframe, search, nextToken, from, to };

    const url = createUrlWithParams('audit', params);
    return this.session.request(url);
  }

  getAvailableCredit(userGuid) {
    const url = createUrlWithParams('requests/available-credit', { userGuid });
    return this.session.request(url);
  }

  getUsedCredit(userGuid) {
    const url = createUrlWithParams('requests/used-credit', { userGuid });
    return this.session.request(url);
  }

  getJobsList(tenantId) {
    return this.session.request(`jobs/${tenantId}`);
  }

  async getCareerItems(type = 'jobs') {
    const { tenantId } = this.session.user;
    const { jobTitles } = await this.session.request(`jobs/${tenantId}`);
    return getTopCareerItems(jobTitles, type);
  }

  getCurrencyConversionRate(from, to) {
    return Currency.getRate(from, to);
  }

  getCustomCondition(tenantId) {
    return this.session.request(`tenants/${tenantId}/custom-content/type/customCondition`);
  }

  getCustomFAQContent(tenantId) {
    return this.session.request(`tenants/${tenantId}/custom-content/type/faq`);
  }

  getCustomHomepageContent(tenantId) {
    return this.session.request(`tenants/${tenantId}/custom-content/type/homepage`);
  }

  getCustomMagicLinkContent(tenantId, lang) {
    return this.session.domainRequest(`login/content/${tenantId}/${lang}`);
  }

  getCustomContent(tenantId, type, language, strict) {
    const url = createUrlWithParams(`tenants/${tenantId}/custom-content/type/${type}`, { language, strict });
    return this.session.request(url);
  }

  getExtraUserInfoRequired(userId) {
    return this.session.domainRequest(`users/extra-info-required/${userId}`);
  }

  getFeatures(tenantId) {
    return this.session.request(`features/${tenantId}`);
  }

  getInProgressActivities() {
    return this.session.request('activities/in-progress');
  }

  getMarketingInfo(userId) {
    return this.session.domainRequest(`login/marketing/${userId}`);
  }

  getSearchTermTitlesBySkillOverlap(tenantId, jobTitleId, term, language = 'en') {
    if (!jobTitleId || jobTitleId === 'undefined') {
      return Promise.resolve([]);
    }
    const url = `jobs/${tenantId}/${jobTitleId}/search/by-skill-overlap`;
    return this.session.request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ term, lang: language }),
    });
  }

  getSearchTermTitleNames(term) {
    return this.session.request('jobs/search', {
      method: 'POST',
      headers,
      body: JSON.stringify({ term }),
    });
  }

  getSimilarTitlesBySkillOverlap(tenantId, jobTitleId, jobTitleName, jobTitleSOC, language = 'en') {
    if (!jobTitleId || jobTitleId === 'undefined') {
      return Promise.resolve([]);
    }
    const params = new URLSearchParams({ jobTitleName, jobTitleSOC, lang: language });
    const url = `jobs/${tenantId}/${jobTitleId}/similar/by-skill-overlap?${params.toString()}`;
    return this.session.request(url, {
      method: 'GET',
      headers,
    });
  }

  getSkillSubcategoriesForTitle(jobTitleId, tenantId, options = {}) {
    if (!jobTitleId || jobTitleId === 'undefined') {
      return Promise.resolve([]);
    }
    const defaults = { limit: 6, minimumSkills: 5, inflateSkills: false };
    const mergedOptions = { ...defaults, ...options };
    const { limit, minimumSkills, inflateSkills, language = 'en' } = mergedOptions;
    const url = createUrlWithParams(
      `jobs/skills/by-subcategory/${tenantId}/${jobTitleId}`,
      { limit, minimumSkills, inflateSkills, lang: language },
    );
    return this.session.request(url, {
      method: 'GET',
      headers,
    });
  }

  getSkillsForTitle(jobTitleId, tenantId) {
    if (!jobTitleId || jobTitleId === 'undefined') {
      return Promise.resolve([]);
    }
    const url = `jobs/title/${jobTitleId}/${tenantId}/skillsForTitle`;
    return this.session.request(url, {
      method: 'GET',
      headers,
    });
  }

  getSkills(filters) {
    return this.session.request('activities/search/skills', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ filters }),
    });
  }

  getSkillById(id) {
    return this.session.request(`skills/${id}`);
  }

  getDetailedSkills(ids, locale) {
    const url = createUrlWithParams('skills/detailed', { locale });
    return this.session.request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ids }),
    });
  }

  getSkillProfile(tenantId, userGuid, lang) {
    const url = createUrlWithParams(`users/skill-profile/${tenantId}/${userGuid}`, { lang });
    return this.session.request(url, {
      method: 'GET',
      headers,
    });
  }

  querySkillsByName(queryText, limit, locale) {
    const url = createUrlWithParams('skills/query', { name: queryText, limit, locale });
    return this.session.request(url);
  }

  querySkillSubcategoriesByName(queryText, limit, locale) {
    return this.session.request('skills/subcategories/query', {
      method: 'POST',
      headers,
      body: JSON.stringify({ name: queryText, limit, locale }),
    });
  }

  getSkillsForCategory(categoryId, locale) {
    const url = createUrlWithParams(`skills/category/${categoryId}`, { locale });
    return this.session.request(url);
  }

  async getUserSetting(key) {
    const value = await this.session.request(`users/settings/${key}`);
    this.session.user.setSetting(key, value);
    return value;
  }

  getVisibleProviders(tenantId) {
    const url = createUrlWithParams(`tenants/${tenantId}/visibility`, { subjectType: 'provider' });
    return this.session.request(url);
  }

  getVisibleSkills(tenantId) {
    const url = createUrlWithParams(`tenants/${tenantId}/visibility`, { subjectType: 'skillstream' });
    return this.session.request(url);
  }

  listTenants(type, filters = {}) {
    const url = createUrlWithParams('tenants', { type, ...filters });
    return this.session.request(url);
  }

  /**
   * Checks if any admin tenant has a specific feature flag enabled.
   * This is useful when you need to check admin features from a provider/employer context.
   *
   * @param {string} featureFlag - The feature flag name to check
   * @returns {Promise<boolean>} - True if any admin tenant has the feature enabled
   */
  async adminHasFeature(featureFlag) {
    if (!featureFlag) return false;

    try {
      const response = await this.session.request(`tenants/admin-feature/${featureFlag}`);
      return response?.enabled === true;
    } catch {
      return false;
    }
  }

  translate({ from, to, text }) {
    const url = createUrlWithParams('/admin/translate', { from, to });
    return this.session.request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text }),
    });
  }

  logEvent(e, includeBaseAuditData) {
    try {
      if (includeBaseAuditData) {
        const baseAuditData = {
          date: new Date().toISOString(),
          initiator: this.session.user.guid,
          subject: this.session.user.guid,
        };
        e = { ...baseAuditData, ...e };
      }
      return this.session.request('event', {
        method: 'POST',
        headers,
        body: JSON.stringify(e),
      });
    } catch {
      return Promise.resolve();
    }
  }

  patchActivity(id, updatedAttributes) {
    return this.session.request(`activities/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updatedAttributes),
    });
  }

  patchActivities(activities) {
    return this.session.request('activities', {
      method: 'PATCH',
      headers,
      body: JSON.stringify(activities),
    });
  }

  removeActivity(activityId) {
    return this.session.request(`activities/${activityId}`, {
      method: 'DELETE',
      headers,
    });
  }

  removeCourses(programId, removedCourses) {
    return this.session.request(`activities/relation/${programId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ removedCourses }),
    });
  }

  saveFeatures(updatedFeatures) {
    return this.session.request('features', {
      method: 'PUT',
      headers,
      body: JSON.stringify(updatedFeatures),
    });
  }

  saveSkillProfile(profile) {
    return this.session.request('users/skill-profile', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ profile }),
    });
  }

  updateSkillProfile(addedCategories, removedCategories, addedSkillIds, removedSkillIds, titles) {
    return this.session.request('users/update-skill-profile', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ addedCategories, removedCategories, addedSkillIds, removedSkillIds, titles }),
    });
  }

  createRole(role) {
    return this.session.request('admin/roles', {
      method: 'POST',
      headers,
      body: JSON.stringify({ role }),
    });
  }

  deleteRole(roleId) {
    return this.session.request(`admin/roles/${roleId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ roleId }),
    });
  }

  updateRole(role) {
    return this.session.request(`admin/roles/${role.roleId}/update`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ role }),
    });
  }

  /**
   * Removes empty arrays and values from the filter object.
   * Merges nocSkills into skills for API filtering.
   */
  prepareFiltersForApiCall(filter) {
    const updatedFilter = { ...filter };

    if (updatedFilter.nocs?.length > 0 && updatedFilter.nocSkills?.length > 0) {
      updatedFilter.nocSkills = updatedFilter.nocSkills.filter(id => id != null && id !== '');
    } else {
      delete updatedFilter.nocSkills;
    }

    if (updatedFilter.skills) {
      updatedFilter.skills = updatedFilter.skills.filter(id => id != null && id !== '');
    }

    Object.keys(updatedFilter).forEach(key => {
      if ((Array.isArray(updatedFilter[key]) && updatedFilter[key].length === 0) || (!updatedFilter[key] && updatedFilter[key] !== 0)) {
        delete updatedFilter[key];
      }
    });
    return updatedFilter;
  }

  searchActivities({ from, size, filters, randomizeOrder = false, category = undefined, sort = undefined, property = undefined, useUserSkillProfile = false, fields, isAutocompleteQuery = false }) {
    const params = {
      from,
      size,
      randomizeOrder,
      filters: this.prepareFiltersForApiCall(filters),
      category,
      useUserSkillProfile,
      fields,
      isAutocompleteQuery,
    };
    if (sort && Object.keys(sort).length) params.sort = sort;
    if (property && Object.keys(property).length) params.property = property;
    const url = createUrlWithParams('activities/search', params);
    return this.session.request(url);
  }

  searchJobTitles({ searchTerm, locale, size } = {}) {
    const url = createUrlWithParams('job-titles/search', { searchTerm, locale, size });
    return this.session.request(url);
  }

  searchTenants({ searchTerm, type, from = 0, size = 5 } = {}) {
    const trimmedSearch = searchTerm?.trim();
    if (!trimmedSearch) {
      return Promise.resolve({ total: 0, hits: [] });
    }

    const params = {
      searchTerm: trimmedSearch,
      type,
      from,
      size: Math.min(Number(size) || 5, 5),
    };
    const url = createUrlWithParams('tenants/search', params);
    return this.session.request(url);
  }

  getStreamOrder({ filters, randomizeOrder = false, dynamicFilters = undefined, defaultStreamIds = undefined }) {
    const url = createUrlWithParams('activities/stream-order', { randomizeOrder, filters: this.prepareFiltersForApiCall(filters), dynamicFilters, defaultStreamIds });
    return this.session.request(url);
  }

  /**
   * Fetches the cached dashboard data for a tenant.
   * Returns pre-computed stream data with activities for the default (unfiltered) dashboard.
   *
   * @param {string} tenantId - The tenant ID to fetch cached dashboard for.
   * @returns {Promise<Object>} Object with streams array, generatedAt timestamp, and fromCache flag.
   */
  getCachedDashboard(tenantId) {
    return this.session.request(`activities/dashboard-cache/${tenantId}`);
  }

  /**
   * Executes multiple activity searches in a single request.
   * Batches multiple stream queries into one API call to reduce network overhead.
   *
   * @param {Array<Object>} streams - Array of stream objects with id, type, category, filters, etc.
   * @param {Object} catalogFilters - Global catalog filters to apply to all streams.
   * @param {boolean} randomizeOrder - Whether to randomize results.
   * @param {Array<string>} fields - Fields to return in activity results.
   * @returns {Promise<Object>} Object with streams array containing search results.
   */
  searchActivitiesBatch({ streams, catalogFilters, randomizeOrder = true, fields }) {
    const preparedStreams = streams.map(stream => ({
      ...stream,
      filters: stream.filters ? this.prepareFiltersForApiCall(stream.filters) : undefined,
    }));

    return this.session.request('activities/dashboard-batch', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        streams: preparedStreams,
        filters: catalogFilters ? this.prepareFiltersForApiCall(catalogFilters) : undefined,
        randomizeOrder,
        fields,
      }),
    });
  }

  exportActivities({ filters, attributes }) {
    return this.session.request('activities/search/export', {
      method: 'POST',
      headers,
      body: JSON.stringify({ filters, attributes }),
    });
  }

  searchLightcast(query, type = 'company') {
    const safeQuery = encodeURIComponent(query);
    const params = new URLSearchParams({ query: safeQuery });

    return this.session.request(`search/${type}?${params.toString()}`);
  }

  updateBulkEditCache(cache) {
    return this.session.request('users/bulk-edit-cache', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ cache }),
    });
  }

  getBulkEditCache(type, version = '1.0') {
    return this.session.request(`users/bulk-edit-cache/${type}/${version}`);
  }

  getIndustryFromCompanyID(companyId) {
    return this.session.request(`search/industry_name/${companyId}`);
  }

  getInvoiceItem(invoiceItemId) {
    return this.session.request(`invoiceItem/${invoiceItemId}`);
  }

  sendSuggestion(activityId, emails, message) {
    return this.session.request(`suggest/${activityId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message, recipients: emails }),
    });
  }

  setAccountConnection(accountConnectInfo) {
    return this.session.request('connection', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ ...accountConnectInfo }),
    });
  }

  setActivityActive(activityId, active) {
    return this.session.request(`activities/${activityId}/active`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ active }),
    });
  }

  setTenantActive(tenantId, active) {
    return this.session.request(`tenants/${tenantId}/active`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ active }),
    });
  }

  setApproval(applicationUuid, approvedAmount, approvalReason, approvalState, isBudgetHolder) {
    return this.session.request(`requests/${applicationUuid}/pending/approvals`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ approvedAmount, approvalReason, approvalState, isBudgetHolder }),
    });
  }

  setCustomContent(tenantId, customContent) {
    return this.session.request(`tenants/${tenantId}/custom-content`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(customContent),
    });
  }

  setDiscountCode(uuid, discountCode) {
    return this.session.request(`requests/${uuid}/discount`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ discountCode }),
    });
  }

  setDocumentTitle(titleText) {
    document.title = titleText;
  }

  setMetaTag(tagName, tagContent) {
    const metaTag = document.querySelector(`meta[name="${tagName}"]`) || document.querySelector(`meta[property="${tagName}"]`);
    if (metaTag) {
      metaTag.content = tagContent;
    } else {
      const newMetaTag = document.createElement('meta');
      newMetaTag.name = tagName;
      newMetaTag.content = tagContent;
      document.head.appendChild(newMetaTag);
    }
  }

  setEntitlements(tenantId, entitlements) {
    return this.session.request(`tenants/${tenantId}/entitlements`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ entitlements }),
    });
  }

  setPaymentDate(applicationUuid) {
    return this.session.request(`requests/${applicationUuid}/payment`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ applicationUuid }),
    });
  }

  async setSessionSetting(key, value) {
    const response = await this.session.request(`session/settings/${key}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ value }),
    });
    this.session.user.settings = response;
    return response;
  }

  async setUser(user) {
    this.session.user = await this.saveUser(user);
    return this.session.user;
  }

  async saveUser(user) {
    const sanitizedUser = { ...user };
    delete sanitizedUser.tenantType;
    delete sanitizedUser.entitlements;

    return await this.session.request('users', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ user: sanitizedUser }),
    });
  }

  async getDisplayName(guid) {
    return await this.session.request(`admin/display_name/${guid}`);
  }

  async getAllRoles() {
    return await this.session.request('admin/roles', {
      method: 'GET',
      headers,
    });
  }

  async getRole(roleId) {
    return await this.session.request(`admin/roles/${roleId}`, {
      method: 'GET',
      headers,
    });
  }

  async getUsersAssignedToRole(roleId) {
    return await this.session.request(`admin/roles/${roleId}/users`, {
      method: 'GET',
      headers,
    });
  }

  async getRolesByTenantId(tenantId) {
    return await this.session.request(`admin/roles/tenant/${tenantId}`, {
      method: 'GET',
      headers,
    });
  }

  async getRoles() {
    return await this.session.request('admin/roles/', {
      method: 'GET',
      headers,
    });
  }

  getDeleteRoleImpact(roleId) {
    return this.session.request(`admin/roles/${roleId}/delete-impact`);
  }

  /**
   * Fetches tasks for the authenticated user
   * @param {Object} [filters] - Optional filters for tasks
   * @param {string} [filters.status] - Filter by task status
   * @param {string} [filters.initiator] - Filter by task initiator
   * @param {string} [filters.startDate] - Filter by start date (ISO format)
   * @param {string} [filters.timeRange] - Filter by time range (e.g. '1min', '24hours')
   * @param {number} [filters.limit] - Limit number of results
   * @returns {Promise<Object>} - Promise resolving to the tasks data
   */
  fetchTasks(filters = {}) {
    return this.session.request(createUrlWithParams('tasks/find', filters));
  }

  /**
   * Fetches a specific task by ID
   * @param {string} taskId - The ID of the task to fetch
   * @returns {Promise<Object>} - Promise resolving to the task data
   */
  fetchTask(taskId) {
    return this.session.request(`tasks/${taskId}`);
  }

  async setUserSetting(key, value) {
    const response = await this.session.request(`users/settings/${key}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ value }),
    });
    const { settings } = response;
    this.session.settings = settings;
    return response;
  }

  setVisibleState({ tenantId, ...visibilityData }) {
    return this.session.request(`tenants/${tenantId}/visibility`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(visibilityData),
    });
  }

  similarTitles(title) {
    return this.session.request(`jobs/title/${title}/similar`, {
      method: 'GET',
      headers,
    });
  }

  skillsByTitleId(id) {
    if (!id || id === 'undefined') {
      return Promise.resolve({ skills: [] });
    }
    return this.session.request(`jobs/title/${id}/skills`, {
      method: 'GET',
      headers,
    });
  }

  async toggleMyListActivity(activityId) {
    const response = await this.session.request(`activities/my-list/${activityId}`, {
      method: 'POST',
      headers,
    });
    this.session.user.settings = response.settings;
    return response;
  }

  async updateTenantImage(tenantId, files) {
    return await this.session.request(`tenants/logo/${tenantId}`, {
      method: 'POST',
      headers: {},
      body: files,
    });
  }

  updateTenantPackage(tenantId, packageName, visibleStreamIds) {
    return this.session.request(`tenants/${tenantId}/visibility/package`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ packageName, visibleStreamIds }),
    });
  }

  createCustomStream(tenantId, streamData) {
    return this.session.request(`tenants/${tenantId}/custom-streams`, {
      method: 'POST',
      headers,
      body: JSON.stringify(streamData),
    });
  }

  updateCustomStream(tenantId, streamId, streamData) {
    return this.session.request(`tenants/${tenantId}/custom-streams/${streamId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(streamData),
    });
  }

  deleteCustomStream(tenantId, streamId) {
    return this.session.request(`tenants/${tenantId}/custom-streams/${streamId}`, {
      method: 'DELETE',
      headers,
    });
  }

  reorderCustomStreams(tenantId, orderedStreamIds) {
    return this.session.request(`tenants/${tenantId}/custom-streams/reorder`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ orderedStreamIds }),
    });
  }

  previewCustomStream(tenantId, streamConfig) {
    return this.session.request(`tenants/${tenantId}/custom-streams/preview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(streamConfig),
    });
  }

  updateBudget(tenant) {
    return this.session.request(`tenants/${tenant.id}/budget`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenant }),
    });
  }

  updateAnalyticsFiscalYearBudget(amount) {
    return this.session.request('report/analytics-fiscal-year-budget', {
      method: 'PUT',
      headers,
      body: JSON.stringify({ amount }),
    });
  }

  updatePLGBudget(tenant) {
    return this.session.request(`plg/${tenant.id}/budget`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenant }),
    });
  }

  completePLGOnboarding(tenantId, details) {
    return this.session.request(`plg/${tenantId}/complete-onboarding`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        tenantDetails: details,
      }),
    });
  }

  updateAttributeRequirements(tenant) {
    return this.session.request(`tenants/${tenant.id}/attribute-requirements`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(tenant.attributeRequirements),
    });
  }

  updateGeneral(tenant) {
    return this.session.request(`tenants/${tenant.id}/general`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenant }),
    });
  }

  updateCustomAttributes(tenantId, customAttributes) {
    return this.session.request(`tenants/${tenantId}/custom-attributes`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ customAttributes }),
    });
  }

  updateApplicationCustomAttributes(tenantId, applicationCustomAttributes) {
    return this.session.request(`tenants/${tenantId}/application-custom-attributes`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ applicationCustomAttributes }),
    });
  }

  updateApplicationAccountConnection(userApplication, accountConnection) {
    const applicationUuid = userApplication.uuid;
    return this.session.request(`requests/${applicationUuid}/account-connection`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ userApplication, accountConnection }),
    });
  }

  updateCareerExplorerInfo(tenantId, careerExplorerInfo) {
    this.session.request(`tenants/${tenantId}/career-explorer`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(careerExplorerInfo),
    });
  }

  upsertActivity(activity, newProgramCourses = []) {
    let promise;
    if (activity.id) {
      promise = this.session.request(`activities/${activity.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(activity),
      });
    } else {
      promise = this.session.request('activities', {
        method: 'POST',
        headers,
        body: JSON.stringify({ activity, children: newProgramCourses }),
      });
    }
    return promise;
  }

  upsertTenantSSO(tenantSSO) {
    return this.session.request(`tenants/${tenantSSO.tenantId}/sso`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenantSSO }),
    });
  }

  async upsertUser(user) {
    const response = await this.session.request(`users/${user.guid}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ user }),
    });
    this.session.user = response;
    return response;
  }

  async updateAuth0User(auth0UserId, properties) {
    return await this.session.request(`users/auth0/patch/${auth0UserId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ properties }),
    });
  }

  async resendVerificationEmail(auth0UserId) {
    return this.session.request('users/auth0/resend-verification-email', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ auth0UserId }),
    });
  }

  getAllUsersByTenantId(tenantId, options = { includeRoles: false }) {
    const { includeRoles } = options;
    const url = createUrlWithParams(`users/tenant/${tenantId}`, { includeRoles });
    return this.session.request(url);
  }

  getUserById(guid) {
    return this.session.request(`users/${guid}`);
  }

  getUserAttributeSources(guid) {
    return this.session.request(`users/${guid}/attribute-sources`);
  }

  async getAllRolesForUser(userId) {
    return await this.session.request(`users/${userId}/roles`, {
      method: 'GET',
      headers,
    });
  }

  updatePermissions(roleId, permissions) {
    return this.session.request(`admin/roles/${roleId}/permissions`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ permissions }),
    });
  }

  async assignARoleToAUser(userId, roleId) {
    return this.session.request(`users/${userId}/role/${roleId}`, {
      method: 'PUT',
      headers,
    });
  }

  /**
   * Assign and Unassign roles for a user
   * @param {string} userId
   * @param {Array<string>} roles - comma separated list of role ids
   */
  async bulkUpdateRoleUserAssignments(userId, roles) {
    return await this.session.request(`users/${userId}/roles`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ roleIds: roles }),
    });
  }

  async unassignARoleFromAUser(userGuid, roleId) {
    return await this.session.request(`users/${userGuid}/roles/${roleId}`, {
      method: 'DELETE',
      headers,
    });
  }

  async toggleUserActiveStatus(user) {
    return await this.session.request(`users/${user.guid}/toggle-active`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ user }),
    });
  }

  // GOALS
  async getUserGoalsByProviderId(providerId, tenantId, userGuid) {
    return await this.session.request(`goals/provider/${providerId}/tenant/${tenantId}/user/${userGuid}`);
  }

  async updateGoal(goal) {
    return this.session.request(`goals/${goal.id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(goal),
    });
  }

  async updateGoalProgress(applicationId, courseId, goalId) {
    return this.session.request(`goals/progress/${goalId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ applicationId, courseId }),
    });
  }

  async getGoalProgresses(tenantId, userGuid) {
    return this.session.request(`goals/progress/tenant/${tenantId}/user/${userGuid}`);
  }

  async getUserGoals(tenantId, userGuid) {
    return this.session.request(`goals/tenant/${tenantId}/user/${userGuid}`);
  }

  async getPaymentMethods(tenantId) {
    return this.session.request(`payment/${tenantId}/payment-methods`);
  }

  async preAuthChargeTenantForApplication(tenantId, applicationId) {
    return this.session.request(`payment/${tenantId}/payment-methods/pre-auth/${applicationId}`, {
      method: 'PATCH',
      headers,
    });
  }

  async getAddPaymentMethodSession(tenantId, params = {}) {
    const searchParams = new URLSearchParams({ origin: window.location.href, ...params });
    return this.session.request(`payment/${tenantId}/payment-methods/add?${searchParams.toString()}`);
  }

  async confirmPaymentMethodAddition(tenantId) {
    return this.session.request(`payment/${tenantId}/payment-methods/add/confirm`);
  }

  async editPaymentMethod(tenantId, paymentMethod) {
    return this.session.request(`payment/${tenantId}/payment-methods/${paymentMethod.id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(paymentMethod),
    });
  }

  async deletePaymentMethod(tenantId, paymentMethodId) {
    return this.session.request(`payment/${tenantId}/payment-methods/${paymentMethodId}`, {
      method: 'DELETE',
      headers,
    });
  }

  lightcastSkillCategories(companyId) {
    const params = new URLSearchParams({ companyId });
    return this.session.request(`lightcast/search/skill_categories_name?${params.toString()}`);
  }

  lightcastLotCareerArea(companyId) {
    const params = new URLSearchParams({ companyId });
    return this.session.request(`lightcast/search/lot_career_area_name?${params.toString()}`);
  }

  lightcastLotOccupationGroupNames() {
    return this.session.request('lightcast/search/lot_occupation_group_names');
  }

  lightcastSkillSubcategoryNames(locale) {
    const url = createUrlWithParams('lightcast/search/skill_subcategories_names', { locale });
    return this.session.request(url);
  }

  lightcastSkills(params) {
    return this.session.request('lightcast/search/skills', {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
  }

  lightcastRecommendedSkills(companyId, params, locale) {
    const urlParams = new URLSearchParams({ companyId });
    if (locale) {
      urlParams.set('locale', locale);
    }
    return this.session.request(`lightcast/search/skill_subcategories?${urlParams.toString()}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
  }

  lightcastGetSkills(skillsDescription, locale) {
    const url = createUrlWithParams('lightcast/skills/extract', { locale });
    return this.session.request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        skillsDescription,
      }),
    });
  }

  createStreams(streams) {
    return this.session.request('activities/streams', {
      method: 'POST',
      headers,
      body: JSON.stringify({ streams }),
    });
  }

  /**
   * Creates any type of task based on the task type
   *
   * @param {string} taskType - The type of task to create
   * @param {Object} [taskData={}] - Parameters for the specific task type
   * @returns {Promise<Object>} - Promise resolving to the created task information
   */
  createTask(taskType, taskData = {}) {
    return this.session.request(`tasks/type/${taskType}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(taskData),
    });
  }

  async getApprovedAndPendingCounts() {
    const url = 'requests/approved-and-pending-counts';
    return this.session.request(url);
  }

  async generateActivityImage(input) {
    const { activityId, additionalContext = '', stylePreset = 'photographic', includeActivityContext = true } = input;
    return this.session.request(`activities/${activityId}/generate-image`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ additionalContext, stylePreset, includeActivityContext }),
    });
  }

  async retrieveSentryConfig() {
    return this.session.request('sentry/config', {
      method: 'GET',
    });
  }

  async getTenantDetails(tenantId) {
    return this.session.request(`plg/${tenantId}/details`, {
      method: 'GET',
      headers,
    });
  }

  async updateTenantDetails(tenantId, details) {
    return this.session.request(`plg/${tenantId}/details`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(details),
    });
  }

  async updateTenantBranding(tenantId, logo) {
    const files = new FormData();
    files.append('logo', logo);
    return this.session.request(`plg/${tenantId}/branding`, {
      method: 'POST',
      headers: {},
      body: files,
    });
  }

  async updateTenantPolicy(tenantId, customCondition) {
    return this.session.request(`plg/${tenantId}/policy`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(customCondition),
    });
  }

  async deactivateUsers(usersCsvFile, { execute } = { execute: false }) {
    const files = new FormData();
    files.append('usersCsvFile', usersCsvFile);
    return this.session.request(`admin/users/deactivate?execute=${execute}`, {
      method: 'POST',
      headers: {},
      body: files,
    });
  }

  /**
   * Gets all NOC (National Occupational Classification) codes.
   * Since there are only ~516 NOCs, we fetch all and let the client filter.
   *
   * @returns {Promise<Array>} - Promise resolving to array of all NOC objects sorted alphabetically
   */
  async getAllNocs() {
    return this.session.request('occupations/nocs');
  }

  /**
   * Gets specialized skills associated with a NOC code.
   *
   * @param {string} nocCode - The 5-digit NOC code (e.g., "21110")
   * @returns {Promise<Array<string>>} - Promise resolving to array of skill IDs
   */
  async getSkillsByNoc(nocCode) {
    return this.session.request(`occupations/nocs/${nocCode}/skills`);
  }

}
